import { useIsOrgManager } from '../../auth/roles';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetCurrentAuthenticatedCoach } from './useGetCurrentAuthenticatedCoach';

/**
 * The dashboard mirror of the API's `hasCoachSelfOrOrganizationAdminAccess`:
 * an org ADMIN manages every coach, a COACH manages only the `Coach` row
 * linked to their own membership.
 *
 * Extracted because the predicate had one inline copy (`CoachDetailsPage`) and
 * the pages that needed a second — `CoachingSessionDetailsPage`, whose Cancel
 * button is rendered for every session on the shared calendar, including other
 * coaches' — did not have it, and so offered an action the API 403s.
 *
 * `coachId` may be undefined while its page is still loading; the answer is
 * then "no" for a coach and still "yes" for an admin, which is the
 * conservative direction for both.
 *
 * The own-coach lookup only fires for coach roles (`useGetCurrentAuthenticatedCoach`
 * is `enabled` on a defined organization id), so an admin costs no request.
 */
export function useCanManageCoach(coachId: string | undefined): boolean {
    const isOrgManager = useIsOrgManager();
    const { selectedOrganization } = useSelectedOrganization();
    const { data: myCoach } = useGetCurrentAuthenticatedCoach(
        isOrgManager ? undefined : selectedOrganization?.id,
    );

    if (isOrgManager) return true;
    return Boolean(coachId) && Boolean(myCoach?.id) && myCoach?.id === coachId;
}
